import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  CircularProgress,
  Divider,
  Chip,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  Email,
  Lock,
  Security,
  AdminPanelSettings,
  Shield,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { UserLogin } from "../../redux/reducer/LoginUser";
import { ToastContainer, toast } from "react-toastify";
import { useFormik } from "formik";
import * as Yup from "yup";
import Logo from "../../assets/LogoNew2.png";
import { getAdminHomePath } from "../../config/adminRoles";
import { ValidateUser } from "../../redux/reducer/ValidateUser";

const loginSchema = Yup.object().shape({
  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),
  password: Yup.string()
    .min(6, "Password must be at least 6 characters")
    .required("Password is required"),
});

const AdminLoginPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showPassword, setShowPassword] = useState(false);

  const adminAuthState = useSelector((state) => state?.adminAuth);
  const loaderState = useSelector((state) => state?.loginUser?.loading) || adminAuthState?.loading;

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: loginSchema,
    onSubmit: (values, { resetForm }) => {
      const loginValues = { ...values, type: "admin" };
      dispatch(UserLogin({ values: loginValues }))
        .unwrap()
        .then((res) => {
          const userData = res?.data?.data;

          // Check if user is admin type
          if (userData?.type !== "admin") {
            toast.error("Access denied. This portal is for administrators only.", {
              position: "top-center",
            });
            resetForm();
            return;
          }

          localStorage.setItem("name", userData?.name);
          localStorage.setItem("user_email", userData?.email);
          localStorage.setItem("ots_token", res?.data?.accessToken);
          localStorage.setItem("is_register", "true");
          localStorage.setItem("is_active", "true");
          sessionStorage.setItem("ots_token", res?.data?.accessToken);
          sessionStorage.setItem("is_register", userData?.is_register);
          dispatch(ValidateUser({}));
          toast.success("Welcome Admin!", { autoClose: 500 });
          // Land on the first page this admin's role can open, which is not
          // always the dashboard. The route guard corrects it once the
          // server's permission list has loaded.
          setTimeout(() => {
            navigate(getAdminHomePath(userData) || "/admin-dashboard");
          }, 800);
        })
        .catch((error) => {
          toast.error(error?.message || "Invalid credentials", {
            position: "top-center",
          });
          resetForm();
        });
    },
  });

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)",
        position: "relative",
        overflow: "hidden",
        p: 2,
      }}
    >
      {/* Background Pattern */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.05,
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}
      />

      {/* Floating Shapes */}
      <Box
        sx={{
          position: "absolute",
          width: 300,
          height: 300,
          borderRadius: "50%",
          background: "linear-gradient(135deg, #e94560 0%, #ff6b6b 100%)",
          filter: "blur(100px)",
          opacity: 0.15,
          top: -100,
          right: -100,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          width: 250,
          height: 250,
          borderRadius: "50%",
          background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
          filter: "blur(100px)",
          opacity: 0.15,
          bottom: -50,
          left: -50,
        }}
      />

      <Paper
        elevation={24}
        sx={{
          width: "100%",
          maxWidth: 440,
          borderRadius: 4,
          overflow: "hidden",
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* Header Section */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)",
            p: 4,
            textAlign: "center",
            position: "relative",
          }}
        >
          {/* Security Badge */}
          <Box
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              bgcolor: "rgba(233, 69, 96, 0.2)",
              px: 1.5,
              py: 0.5,
              borderRadius: 2,
            }}
          >
            <Shield sx={{ fontSize: 14, color: "#e94560" }} />
            <Typography variant="caption" sx={{ color: "#e94560", fontWeight: 600 }}>
              SECURE
            </Typography>
          </Box>

          {/* Logo */}
          <Box
            sx={{
              display: "inline-block",
              mb: 2,
              transition: "transform 0.3s",
              "&:hover": { transform: "scale(1.05)" },
            }}
          >
            <img src={Logo} alt="OTS Logo" style={{ height: 90,width:150 }} />
          </Box>

          {/* Title */}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, mb: 1 }}>
            <AdminPanelSettings sx={{ color: "#e94560", fontSize: 32 }} />
            <Typography variant="h4" sx={{ color: "#fff", fontWeight: 700 }}>
              Admin Portal
            </Typography>
          </Box>

          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.6)" }}>
            Secure access for administrators only
          </Typography>
        </Box>

        {/* Form Section */}
        <Box sx={{ p: 4, bgcolor: "#fff" }}>
          <form onSubmit={formik.handleSubmit}>
            {/* Email Field */}
            <TextField
              fullWidth
              name="email"
              label="Admin Email"
              placeholder="Enter your admin email"
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email sx={{ color: "#9ca3af" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 2.5,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  bgcolor: "#f8fafc",
                  "&:hover fieldset": { borderColor: "#e94560" },
                  "&.Mui-focused fieldset": { borderColor: "#e94560" },
                },
                "& .MuiInputLabel-root.Mui-focused": { color: "#e94560" },
              }}
            />

            {/* Password Field */}
            <TextField
              fullWidth
              name="password"
              label="Password"
              type={showPassword ? "text" : "password"}
              placeholder="Enter your password"
              value={formik.values.password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.password && Boolean(formik.errors.password)}
              helperText={formik.touched.password && formik.errors.password}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock sx={{ color: "#9ca3af" }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      sx={{ color: "#9ca3af" }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 3,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  bgcolor: "#f8fafc",
                  "&:hover fieldset": { borderColor: "#e94560" },
                  "&.Mui-focused fieldset": { borderColor: "#e94560" },
                },
                "& .MuiInputLabel-root.Mui-focused": { color: "#e94560" },
              }}
            />

            {/* Login Button */}
            <Button
              fullWidth
              type="submit"
              variant="contained"
              disabled={loaderState}
              sx={{
                py: 1.5,
                borderRadius: 2,
                fontSize: 16,
                fontWeight: 600,
                textTransform: "none",
                background: "linear-gradient(135deg, #e94560 0%, #ff6b6b 100%)",
                boxShadow: "0 4px 15px rgba(233, 69, 96, 0.4)",
                "&:hover": {
                  background: "linear-gradient(135deg, #d13652 0%, #e94560 100%)",
                  boxShadow: "0 6px 20px rgba(233, 69, 96, 0.5)",
                },
                "&:disabled": {
                  background: "#e5e7eb",
                },
              }}
            >
              {loaderState ? (
                <CircularProgress size={24} sx={{ color: "#fff" }} />
              ) : (
                <>
                  <Security sx={{ mr: 1 }} />
                  Secure Login
                </>
              )}
            </Button>
          </form>

          {/* Security Notice */}
          <Box sx={{ mt: 4 }}>
            <Divider sx={{ mb: 2 }}>
              <Chip
                icon={<Shield sx={{ fontSize: 14 }} />}
                label="Security Notice"
                size="small"
                sx={{
                  bgcolor: "#fef3c7",
                  color: "#92400e",
                  fontWeight: 500,
                  "& .MuiChip-icon": { color: "#92400e" },
                }}
              />
            </Divider>
            <Typography
              variant="caption"
              sx={{
                color: "#64748b",
                textAlign: "center",
                display: "block",
                lineHeight: 1.6,
              }}
            >
              This portal is restricted to authorized administrators.
              <br />
              Unauthorized access attempts are logged and monitored.
            </Typography>
          </Box>
        </Box>

        {/* Footer */}
        <Box
          sx={{
            py: 2,
            px: 4,
            bgcolor: "#f8fafc",
            borderTop: "1px solid #e5e7eb",
            textAlign: "center",
          }}
        >
          <Typography variant="caption" sx={{ color: "#94a3b8" }}>
            OTS Admin Portal v2.0 | Protected by Advanced Security
          </Typography>
        </Box>
      </Paper>

      <ToastContainer
        position="top-center"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
        style={{ zIndex: 9999 }}
        toastStyle={{
          borderRadius: "8px",
          fontSize: "14px",
        }}
      />
    </Box>
  );
};

export default AdminLoginPage;
